import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import tw from 'twin.macro';

import { negationOptions } from './SegmentCreatorItem';
import { Dropdown, IDropdownOption, Option } from '@components/Dropdown';
import { IAB_BRAND_SAFETY_CATEGORY_ARRAY } from '@insights_module/constants/garm';
import {
  SegmentCreatorData,
  useSegmentCreatorContext,
} from '@insights_module/context/SegmentCreatorContext';
import { CategoryType, IabBrandSafetyRiskLevelType } from '@insights_module/types/BrandSafety';
import { AQLQueryCondition, AQLQueryType, AQLRangeCondition } from '@insights_module/adc_ql/types';
import { useFilterTypeOptions } from '@insights_module/hooks/useFilterTypeOptions';

const Container = tw.div`flex gap-1 items-center h-full`;
const LabelContainer = tw.div`flex items-center h-[28px] text-text-and-icon-muted text-[11px] px-2 py-[3px] rounded shadow-dropdown select-none`;

export const SegmentCreatorIabBrandSafetyItem = ({
  segmentData,
}: {
  segmentData: SegmentCreatorData;
}) => {
  const { category, negation, condition, value } = segmentData;
  const { t } = useTranslation();
  const { pushSegmentData } = useSegmentCreatorContext();
  const { dropdownOptions } = useFilterTypeOptions();

  const brandSafetyOptions: IDropdownOption[] = IAB_BRAND_SAFETY_CATEGORY_ARRAY.map((item) => ({
    value: item,
    label: <Option>{t(`insights.iab_brand_safety_categories.${item}`)}</Option>,
  }));

  brandSafetyOptions.unshift({
    label: (
      <Option tw='font-semibold'>
        {t('insights.query_builder.iab_brand_safety.all_categories')}
      </Option>
    ),
    value: 'all-iab-categories',
    hasDivider: true,
  } as IDropdownOption);

  const emptyBrandSafetyOption: IDropdownOption = {
    value: null,
    label: (
      <Option tw='text-muted2 opacity-50'>
        {t('insights.query_builder.iab_brand_safety.empty_option')}
      </Option>
    ),
  };

  const conditionOptions: IDropdownOption[] = [
    { value: 'range', label: <Option>{t('insights.query_builder.conditions.range')}</Option> },
    { value: 'eq', label: <Option>{t('insights.query_builder.conditions.exact')}</Option> },
  ];

  const riskLevelOptions: IDropdownOption[] = [
    {
      value: 'unrelated',
      label: (
        <Option tw='text-garm-unrelated-dark font-bold'>
          {t('insights.iab_brand_safety_risk_levels.unrelated')}
        </Option>
      ),
    },
    {
      value: 'likely unrelated',
      label: (
        <Option tw='text-garm-low-dark font-bold'>
          {t('insights.iab_brand_safety_risk_levels.likely_unrelated')}
        </Option>
      ),
    },
    {
      value: 'likely related',
      label: (
        <Option tw='text-garm-medium-dark font-bold'>
          {t('insights.iab_brand_safety_risk_levels.likely_related')}
        </Option>
      ),
    },
    {
      value: 'related',
      label: (
        <Option tw='text-garm-high-dark font-bold'>
          {t('insights.iab_brand_safety_risk_levels.related')}
        </Option>
      ),
    },
  ];

  const [selectedType, setSelectedType] = useState<IDropdownOption | null>(
    dropdownOptions.find((item) => item.value === segmentData.type) || dropdownOptions[0]
  );
  const [selectedCategory, setSelectedCategory] = useState<IDropdownOption | null>(
    brandSafetyOptions.find((item) => item.value === category) || null
  );
  const [isNegation, setIsNegation] = useState<boolean>(negation || false);
  const [selectedCondition, setSelectedCondition] = useState<IDropdownOption | null>(
    conditionOptions.find((item) => item.value === (condition as unknown as string)) ||
      conditionOptions[0]
  );
  const [selectedFromValue, setSelectedFromValue] = useState<IDropdownOption | null>(
    riskLevelOptions.find(
      (item) =>
        item.value === (value as AQLRangeCondition<IabBrandSafetyRiskLevelType>)?.gte?.toLowerCase()
    ) || riskLevelOptions[0]
  );
  const [selectedToValue, setSelectedToValue] = useState<IDropdownOption | null>(
    riskLevelOptions.find(
      (item) =>
        item.value === (value as AQLRangeCondition<IabBrandSafetyRiskLevelType>)?.lte?.toLowerCase()
    ) || riskLevelOptions[riskLevelOptions.length - 1]
  );
  const [selectedEqValue, setSelectedEqValue] = useState<IDropdownOption | null>(
    riskLevelOptions.find((item) => item.value === value) ||
      riskLevelOptions[riskLevelOptions.length - 1]
  );

  const [categoryAutoFocus, setCategoryAutoFocus] = useState<boolean>(false);

  useEffect(() => {
    if (selectedType) {
      const type = selectedType.value as string;
      pushSegmentData({
        ...segmentData,
        type: type as AQLQueryType,
        category: null,
        value: null,
        condition: null,
        negation: false,
      });
    }
  }, [selectedType]);

  useEffect(() => {
    if (!(selectedCondition && selectedCategory)) return;

    const condition = selectedCondition.value as unknown as AQLQueryCondition;
    const category = selectedCategory.value as CategoryType | 'all-iab-categories';
    if (selectedCondition?.value === 'range' && selectedFromValue && selectedToValue) {
      const value = {
        gte: selectedFromValue.value as IabBrandSafetyRiskLevelType,
        lte: selectedToValue.value as IabBrandSafetyRiskLevelType,
      };
      pushSegmentData({ ...segmentData, condition, value, category, negation: isNegation });
    }
    if (selectedCondition?.value === 'eq' && selectedEqValue) {
      const value = selectedEqValue.value as IabBrandSafetyRiskLevelType;
      pushSegmentData({ ...segmentData, condition, value, category, negation: isNegation });
    }
  }, [
    selectedCondition,
    selectedFromValue,
    selectedToValue,
    selectedEqValue,
    isNegation,
    selectedCategory,
  ]);

  useEffect(() => {
    if (!category) {
      setCategoryAutoFocus(true);
    }
  }, []);

  return (
    <Container>
      <Dropdown
        options={dropdownOptions}
        value={selectedType}
        onChange={(val) => setSelectedType(val as IDropdownOption)}
        labelStyle={tw`bg-spec-BrandSafetyIAB-filter-bg border border-spec-BrandSafetyIAB-filter-stroke text-spec-BrandSafetyIAB-filter-text font-semibold`}
        caretStyle={tw`fill-spec-BrandSafetyIAB-filter-stroke`}
        dropdownMenuStyle={tw`w-[178px]`}
        isPresentational={true}
      />

      <LabelContainer>{t('insights.query_builder.iab_brand_safety.for')}</LabelContainer>

      <Dropdown
        options={brandSafetyOptions}
        autoFocus={categoryAutoFocus}
        isSearchable={true}
        searchPlaceholder={t('insights.query_builder.iab_brand_safety.search_placeholder')}
        nullable={true}
        nullableOption={emptyBrandSafetyOption}
        value={selectedCategory}
        onChange={(val) => setSelectedCategory(val as IDropdownOption)}
        dropdownMenuStyle={tw`w-[235px]`}
        tw='max-w-[235px]'
        isPresentational={true}
      />
      {!!selectedCategory?.value && (
        <>
          <Dropdown
            options={negationOptions}
            value={isNegation ? negationOptions[1] : negationOptions[0]}
            onChange={(val) => setIsNegation((val as IDropdownOption).value === 'is_not')}
            isSearchable={false}
            isPresentational={true}
          />

          <Dropdown
            options={conditionOptions}
            value={selectedCondition}
            onChange={(val) => setSelectedCondition(val as IDropdownOption)}
            isSearchable={false}
            isPresentational={true}
          />
          {selectedCondition?.value === 'range' && (
            <>
              <Dropdown
                options={riskLevelOptions}
                value={selectedFromValue}
                onChange={(val) => setSelectedFromValue(val as IDropdownOption)}
                isPresentational={true}
              />
              <Dropdown
                options={riskLevelOptions}
                value={selectedToValue}
                onChange={(val) => setSelectedToValue(val as IDropdownOption)}
                isPresentational={true}
              />
            </>
          )}
          {selectedCondition?.value === 'eq' && (
            <Dropdown
              options={riskLevelOptions}
              value={selectedEqValue}
              onChange={(val) => setSelectedEqValue(val as IDropdownOption)}
              isPresentational={true}
            />
          )}
        </>
      )}
    </Container>
  );
};
